import React from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FAQSchema } from './StructuredData';

interface FAQItem {
  question: string;
  answer: string;
  category?: string;
}

interface AEOFAQProps {
  faqs: FAQItem[];
  title?: string;
  showCategories?: boolean;
}

const AEOFAQ: React.FC<AEOFAQProps> = ({
  faqs,
  title = "Frequently Asked Questions",
  showCategories = false
}) => {
  // Group FAQs by category if showCategories is true
  const groupedFaqs = showCategories
    ? faqs.reduce((acc, faq) => {
        const category = faq.category || 'General';
        if (!acc[category]) acc[category] = [];
        acc[category].push(faq);
        return acc;
      }, {} as Record<string, FAQItem[]>)
    : { 'All': faqs };

  return (
    <>
      {/* FAQ Schema for Search Engines */}
      <FAQSchema faqs={faqs} />

      <Box sx={{ my: 4 }}>
        <Typography variant="h2" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
          {title}
        </Typography>

        {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
          <Box key={category} sx={{ mb: 3 }}>
            {showCategories && category !== 'All' && (
              <Typography variant="h3" component="h3" sx={{ mb: 2, color: 'primary.main' }}>
                {category}
              </Typography>
            )}

            {categoryFaqs.map((faq, index) => (
              <Accordion key={index} sx={{ mb: 1 }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`faq-${category}-${index}-content`}
                  id={`faq-${category}-${index}-header`}
                >
                  <Typography variant="h6" component="h3">
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" component="div">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))}
      </Box>
    </>
  );
};

// Pre-defined FAQ data for GoTruTax
export const taxComplianceFAQs: FAQItem[] = [
  {
    question: "What is sales tax nexus and why is it important?",
    answer: "Sales tax nexus refers to the connection between a business and a state that requires the business to collect and remit sales tax. It's crucial because failing to establish nexus where required can result in significant penalties, back taxes, and legal issues. GoTruTax automatically monitors and determines your nexus obligations across all 50 states.",
    category: "Nexus & Compliance"
  },
  {
    question: "How does GoTruTax help with multi-state tax compliance?",
    answer: "GoTruTax uses AI-powered automation to track your business activities across all states, automatically calculate tax obligations, generate compliant filings, and ensure you're collecting the right amount of sales tax in each jurisdiction. Our platform integrates with your existing systems to provide real-time compliance monitoring.",
    category: "Multi-State Compliance"
  },
  {
    question: "What types of businesses benefit from GoTruTax?",
    answer: "GoTruTax is designed for e-commerce businesses, marketplace sellers, SaaS companies, and any business with sales tax obligations. Whether you're a small online retailer or a large enterprise with complex operations, our platform scales to meet your compliance needs.",
    category: "Business Types"
  },
  {
    question: "How accurate are GoTruTax's tax calculations?",
    answer: "GoTruTax achieves 99.9% accuracy in tax calculations by combining AI-powered algorithms with continuously updated tax rate databases and compliance rules. Our system is trained on millions of transactions and undergoes regular audits to ensure precision.",
    category: "Accuracy & Reliability"
  },
  {
    question: "Does GoTruTax integrate with my existing platforms?",
    answer: "Yes, GoTruTax integrates with major e-commerce platforms including Shopify, WooCommerce, BigCommerce, and payment processors like Stripe and Square. We also support QuickBooks, Xero, and other accounting software for seamless workflow integration.",
    category: "Integrations"
  },
  {
    question: "What is economic nexus and how does it affect my business?",
    answer: "Economic nexus is established when your business has sufficient economic presence in a state, typically through sales volume thresholds. GoTruTax monitors your sales data in real-time and alerts you when you meet economic nexus thresholds, ensuring timely registration and compliance.",
    category: "Nexus & Compliance"
  },
  {
    question: "How does GoTruTax handle tax rate changes?",
    answer: "Our platform automatically updates tax rates as they change across all jurisdictions. We monitor legislative changes, rate adjustments, and new tax laws, ensuring your calculations always reflect the most current rates without manual intervention.",
    category: "Updates & Maintenance"
  },
  {
    question: "What kind of support does GoTruTax provide?",
    answer: "GoTruTax offers comprehensive support including AI-powered tax advisors, dedicated account managers for enterprise clients, extensive documentation, and community forums. Our support team includes certified tax professionals and compliance experts.",
    category: "Support & Training"
  },
  {
    question: "How secure is my tax data with GoTruTax?",
    answer: "GoTruTax employs bank-level security with 256-bit SSL encryption, SOC 2 Type II compliance, and GDPR adherence. Your sensitive tax data is protected with multiple layers of security, regular security audits, and compliance with all relevant data protection regulations.",
    category: "Security & Privacy"
  },
  {
    question: "Can GoTruTax help with tax audits?",
    answer: "Yes, GoTruTax provides comprehensive audit support including detailed transaction records, compliance documentation, and audit-ready reports. Our platform maintains complete audit trails and can generate customized reports for tax authorities.",
    category: "Audit Support"
  }
];

export const pricingFAQs: FAQItem[] = [
  {
    question: "What are the different pricing tiers for GoTruTax?",
    answer: "GoTruTax offers three pricing tiers: Monitor (free for basic tax monitoring), Standard ($49/month for growing businesses with full compliance features), and Pro ($149/month for enterprises with advanced automation and priority support). Each tier includes core tax compliance features with increasing levels of automation and support. Get started with our free Monitor plan today!",
    category: "Pricing"
  },
  {
    question: "How much does GoTruTax cost?",
    answer: "Our pricing starts with a free Monitor tier for basic tax monitoring and compliance tracking. The Standard plan is $49/month for comprehensive tax automation, while the Pro plan offers enterprise-grade features for $149/month. All plans include multi-state tax compliance, automated calculations, and filing support. Contact our sales team for custom enterprise pricing.",
    category: "Pricing"
  },
  {
    question: "What's included in the Monitor tier?",
    answer: "The free Monitor tier includes basic tax compliance monitoring, nexus determination alerts, and access to our tax rate database. Perfect for small businesses getting started with sales tax compliance. Upgrade to Standard or Pro anytime to unlock full automation features. Get started for free today!",
    category: "Pricing"
  },
  {
    question: "What's included in the Standard plan?",
    answer: "The Standard plan ($49/month) includes everything in Monitor plus automated tax calculations, multi-platform integrations, transaction tracking, and quarterly tax filing assistance. Ideal for growing businesses processing $50K-$500K in monthly sales. Talk to our sales team for a personalized demo.",
    category: "Pricing"
  },
  {
    question: "What's included in the Pro plan?",
    answer: "The Pro plan ($149/month) includes all Standard features plus advanced automation, priority support, custom integrations, audit protection, and dedicated account management. Designed for enterprises processing over $500K monthly. Contact sales for a custom quote and enterprise features.",
    category: "Pricing"
  },
  {
    question: "Is there a free trial available?",
    answer: "Yes, GoTruTax offers a 14-day free trial of our Standard plan with full access to all features including automated tax calculations and integrations. No credit card required to start. During the trial, you can process transactions, test integrations, and experience the full platform capabilities. Get started today!",
    category: "Trial & Onboarding"
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and ACH bank transfers. Enterprise customers can also arrange invoicing terms with net-30 payment terms. All payments are processed securely through PCI-compliant gateways with enterprise-grade security.",
    category: "Billing"
  },
  {
    question: "Can I change my plan at any time?",
    answer: "Yes, you can upgrade or downgrade your plan at any time from Monitor to Standard to Pro. Changes take effect immediately for upgrades, while downgrades apply at the next billing cycle. We provide prorated billing to ensure you only pay for the features you use. Contact support for plan changes.",
    category: "Plan Management"
  },
  {
    question: "Do you offer discounts for annual billing?",
    answer: "Yes, we offer a 15% discount for annual billing on Standard and Pro plans. Enterprise customers receive additional volume discounts. Contact our sales team to discuss annual pricing options and custom enterprise agreements.",
    category: "Billing"
  },
  {
    question: "How do I get a quote for enterprise pricing?",
    answer: "For custom enterprise pricing with advanced features, dedicated support, and volume discounts, please contact our sales team. We'll assess your specific needs and provide a tailored quote. Enterprise plans start at higher transaction volumes with custom feature sets.",
    category: "Enterprise"
  }
];

export default AEOFAQ;
